import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import {
    Badge,
    DOMView,
    DropArea,
    Interactive,
    type PaletteDef,
    sizes,
    themedFonts,
    useCurrentTheme,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, type IconDefinition, LoadingIndicator } from '@gitbook/spine-react';
import { assertIsDefined, assertNever, filterOutNullable } from '@gitbook/util-ts';

import {
    DIFF_TEXT_COLORS,
    type DNDItem,
    getDragItem,
    PageIcon,
    type UIResolvedContentReference,
    useContentNavigation,
    useContentReference,
    useDroppableWithCallback,
    useImportRuns,
} from '../../../Components';
import { withPageCommands } from '../Page/withPageCommands';
import { PagesRenderer, usePagesToRender } from './PagesRenderer';
import styles from './TableOfContents.module.css';
import { ToCInsertionLine } from './ToCInsertionLine';
import { ToCMenuItem } from './ToCMenuItem';
import type { PageToCItemProps, TableOfContentsProps } from './types';

/**
 * Item in the navigation for a page. It also renders the children.
 */
export function PageToCItem(props: PageToCItemProps & TableOfContentsProps): React.ReactElement {
    const { treePage, readOnly, adaptiveContentContext } = props;

    const shouldUsePalette =
        treePage.diff !== hiveCore.PageDiffStatus.Deleted &&
        ((treePage.page.condition && adaptiveContentContext) || (!readOnly && !props.computedPage));

    return shouldUsePalette ? (
        <PageToCItemWithPalette {...props} page={treePage.page} />
    ) : (
        <PageToCItemMaybeLink {...props} palette={undefined} />
    );
}

/**
 * Render the page item with the editing commands.
 */
const PageToCItemWithPalette = withPageCommands<
    PageToCItemProps &
        TableOfContentsProps & {
            page: hiveCore.RevisionPage;
        }
>(function PageToCItemWithPaletteImpl(props) {
    const { commandsElement, commandsPalette, siteAudience } = props;

    return (
        <>
            {commandsElement}
            <PageToCItemMaybeLink
                {...props}
                palette={commandsPalette}
                onClickPageCondition={siteAudience?.onClickCondition}
            />
        </>
    );
});

function PageToCItemMaybeLink(
    props: {
        palette: (() => PaletteDef) | undefined;
        onClickPageCondition?: () => void;
    } & PageToCItemProps &
        TableOfContentsProps
) {
    const { treePage, onClickPageCondition } = props;

    const defaultItem = (
        <PageToCItemImplementation
            {...props}
            contentRef={undefined}
            onClickPageCondition={onClickPageCondition}
        />
    );

    if (hiveCore.isLinkTreePage(treePage)) {
        return (
            <React.Suspense fallback={defaultItem}>
                <PageToCItemWithLink {...props} treePage={treePage} />
            </React.Suspense>
        );
    }

    return defaultItem;
}

/**
 * When the page is a link, we need to resolve the content reference to get the target.
 * It can suspend if the content reference is not resolved yet.
 */
function PageToCItemWithLink(
    props: {
        palette: (() => PaletteDef) | undefined;
    } & PageToCItemProps<hiveCore.RevisionPageLink> &
        TableOfContentsProps
) {
    const { treePage } = props;
    const { page } = treePage;
    const contentRef = useContentReference(page.target as api.ContentRef);

    return (
        <PageToCItemImplementation
            {...props}
            contentRef={contentRef}
            onClickPageCondition={undefined}
        />
    );
}

/**
 * Actual implementation of the page item.
 */
function PageToCItemImplementation(
    props: {
        contentRef: UIResolvedContentReference | undefined;
        palette: (() => PaletteDef) | undefined;
        onClickPageCondition: (() => void) | undefined;
    } & PageToCItemProps &
        TableOfContentsProps
) {
    const {
        space,
        revision,
        changeRequest,
        treePage,
        parent,
        currentPage,
        readOnly,
        palette,
        contentRef,
        index,
        numberOfPages,
        flatConflictedSubtree,
        isParentHidden,
        showIfHidden,
        adaptiveContentContext,
        onClickPageCondition,
        isComputedRoot,
        computedPage,
        isFirstOfGroup,
        analyticsContext,
    } = props;
    const { page } = treePage;

    const isLast = index === numberOfPages - 1;
    const isFirst = index === 0;
    const currentPageId = currentPage ? currentPage.id : null;

    const { getNavigationHref, navigateToPage } = useContentNavigation();
    const { sourceRevision } = useHiveRevision();

    const isHiddenPage = (() => {
        if (hiveCore.isRevisionPageLink(page) || hiveCore.isRevisionPageComputed(page)) {
            return false;
        }

        return Boolean(page.hidden);
    })();

    // Don't dim if there's a diff.
    const dimmed = isHiddenPage && !treePage.diff;

    const pageImportRuns = useImportRuns({
        target: {
            space: space.id,
            changeRequest: props.changeRequest?.id ?? null,
            page: page.id,
        },
    });

    const subPages = usePagesToRender({
        parent: treePage,
        showHiddenPages: showIfHidden,
        importRuns: pageImportRuns,
    });

    const hasSubPages =
        subPages.numberOfPages > 0 ||
        subPages.groups.some((group) => group.computedPage || group.importRun);

    const active = currentPageId === page.id;
    const isDescendantActive = currentPageId
        ? hiveCore.isPageDescendant(revision, page.id, currentPageId)
        : false;

    const [opened, setOpened] = React.useState(isDescendantActive);
    const hasChildrenDisplayed =
        (opened || page.type === hiveCore.RevisionPageType.Group) && hasSubPages;

    const [, font] = useCurrentTheme(themedFonts);
    const hiveBranch = useHiveBranch();

    const interactive = Interactive.useInteractiveState({
        hoverDelayIn: 200,
        detectHoverZone: true,
    });

    const commandsButtonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(commandsButtonRef);

    const href = React.useMemo(() => {
        if (page.type === hiveCore.RevisionPageType.Link) {
            return contentRef?.href;
        }

        if (page.type === hiveCore.RevisionPageType.Computed) {
            // TODO
            return '#';
        }

        if (treePage.diff === hiveCore.PageDiffStatus.Deleted) {
            return;
        }

        return getNavigationHref(revision, page.id);
    }, [page.type, page.id, treePage.diff, getNavigationHref, revision, contentRef?.href]);

    // All pages are draggable
    const drag = useDraggable({
        disabled: readOnly || !!computedPage,
        ...getDragItem({
            type: 'page',
            id: page.id,
            pageId: page.id,
            space: space.id,
            previewLabel: page.title,
            previewIcon: 'FileText',
        }),
    });

    // Drop element in the page
    const drop = useDroppableWithCallback({
        shouldAccept: React.useCallback(
            (item) => {
                if (readOnly) {
                    return false;
                }

                switch (item.type) {
                    case 'page': {
                        return hiveCore.isPageMoveableInRevisionUnder(
                            sourceRevision,
                            item.pageId,
                            page.id
                        );
                    }
                    case 'space':
                    case 'collection':
                        return true;
                    default:
                        return false;
                }
            },
            [sourceRevision, page.id, readOnly]
        ),
        onDrop: (item: DNDItem) => {
            assertIsDefined(hiveBranch);

            switch (item.type) {
                case 'page': {
                    hiveBranch.change((change) => {
                        change.movePage(item.pageId, page.id);
                    });
                    break;
                }
                case 'space':
                case 'collection': {
                    const target: api.ContentRefSpace | api.ContentRefCollection =
                        item.type === 'space'
                            ? {
                                  kind: 'space',
                                  space: item.id,
                              }
                            : {
                                  kind: 'collection',
                                  collection: item.id,
                              };

                    hiveBranch.change((change) => {
                        change.insertPage(
                            hiveCore.createPageLink({
                                title: item.title,
                                target,
                            }),
                            { into: page.id }
                        );
                    });
                    break;
                }
            }
        },
    });
    const dropTargetPage = computedPage ?? page;

    // When hovering for more than 1s, we expand children.
    const onDropHover = () => {
        setOpened(true);
    };

    // User is pressing the entry
    const onPress = (event: React.MouseEvent) => {
        if (drag.isDragging) {
            // if we were dragging, we don't navigate
            return;
        }

        if (event.isDefaultPrevented()) {
            return;
        }

        // if they want to open in a new tab, let them
        // @ts-ignore
        if (event.nativeEvent.metaKey || event.nativeEvent.ctrlKey) {
            // browser will handle it
            return;
        }

        event.preventDefault();

        if (page.type === hiveCore.RevisionPageType.Document) {
            navigateToPage(revision, page.id, { preserveSideSheet: true, preserveComments: false });
        } else if (page.type === hiveCore.RevisionPageType.Link) {
            contentRef?.open();
        }
    };

    // When navigating to a page that is under this one, we open the tree
    React.useEffect(() => {
        if (isDescendantActive) {
            setOpened(true);
        }
    }, [isDescendantActive]);

    const highlighting = getHighlightingForPage(revision, treePage, flatConflictedSubtree);

    // If it's hidden, read-only and the user doesn't have edit permissions, we don't render it.
    // We purposefully don't render hidden pages in read-only mode to replicate the published content experience.
    const hidden = (isHiddenPage || isParentHidden) && !showIfHidden;
    if (hidden) {
        return null;
    }

    const visiblityProps = getVisibilityProps({
        visiblityStatus: isHiddenPage ? 'hidden' : 'visible',
        indexStatus: getTreePageIndexStatus(revision, treePage),
    });

    const canInsertGroup =
        !parent || (parent.type === hiveCore.RevisionPageType.Group && page.id !== parent.pages[0]);

    return (
        <div
            className={clsx(
                page.type === hiveCore.RevisionPageType.Group
                    ? // Groups have 24px of y spacing (except if group is first in ToC: no top spacing)
                      isFirst
                        ? 'mb-6'
                        : 'my-6'
                    : // Other ToC items have 4px of y spacing, except
                      // Single item: no spacing
                      // First item: no top spacing
                      // Last item: no bottom spacing
                      isFirst && isLast
                      ? 'my-0'
                      : isFirst
                        ? 'mb-1'
                        : isLast
                          ? 'mt-1'
                          : 'my-1'
            )}
        >
            <DropArea ref={drop.setNodeRef} dropHovered={drop.isOver} onDropHover={onDropHover}>
                <DOMView ref={drag.setNodeRef} {...interactive.events} {...drag.listeners}>
                    {!readOnly && (!computedPage || (isFirstOfGroup && isComputedRoot)) ? (
                        <ToCInsertionLine
                            page={dropTargetPage}
                            position="before"
                            visible={interactive.hovered && interactive.hoverZone.top}
                            canInsertGroup={canInsertGroup}
                        />
                    ) : null}
                    <ToCMenuItem
                        kind={
                            page.type === hiveCore.RevisionPageType.Group
                                ? 'group'
                                : !parent || parent.type === hiveCore.RevisionPageType.Group
                                  ? 'primary'
                                  : 'secondary'
                        }
                        dimmed={dimmed}
                        leftIcon={getIconForHighlighting(highlighting)}
                        leftIconTooltip={changeRequest && getChangesTooltipText(highlighting)}
                        leftIconStyle={getClassNameForHighlighting(highlighting)}
                        icon={
                            page.emoji || page.icon ? (
                                <PageIcon page={page} size={sizes.ICON_200} />
                            ) : undefined
                        }
                        rightIcons={[
                            visiblityProps
                                ? {
                                      iconTestId: 'hidden-page-icon',
                                      iconComponent: visiblityProps.icon,
                                      iconTooltip: visiblityProps.label,
                                  }
                                : undefined,

                            // Page link indicator
                            page.type === hiveCore.RevisionPageType.Link
                                ? {
                                      iconComponent: contentRef
                                          ? contentRef.secondaryIcon
                                          : LoadingIndicator,
                                      iconTooltip:
                                          page.type === hiveCore.RevisionPageType.Link && contentRef
                                              ? contentRef.secondaryIconDescription
                                              : undefined,
                                  }
                                : undefined,
                        ].filter(filterOutNullable)}
                        active={active}
                        onPress={
                            page.type === hiveCore.RevisionPageType.Group ? undefined : onPress
                        }
                        disabled={changeRequest && highlighting === hiveCore.PageDiffStatus.Deleted}
                        textStyle={clsx(
                            highlighting === hiveCore.RevisionConflictState.DocumentConflict
                                ? 'text-merge'
                                : null,
                            highlighting === hiveCore.RevisionConflictState.DeletedInBase ||
                                highlighting === hiveCore.RevisionConflictState.DeletedInHead
                                ? 'text-danger'
                                : null,
                            highlighting === 'deleted-in-conflicted-subtree' && font.textMuted,
                            highlighting ===
                                hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead ||
                                highlighting ===
                                    hiveCore.RevisionConflictState
                                        .DeletedInHeadSubtreeModifiedInBase
                                ? 'text-danger'
                                : null
                        )}
                        href={href}
                        buttons={
                            palette ? (
                                <>
                                    {
                                        // Adaptive content page condition indicator
                                        adaptiveContentContext && page.condition ? (
                                            <Button
                                                label={`This page is conditionally shown to visitors. Click to ${readOnly ? 'view' : 'edit'} its condition.`}
                                                kind="blank"
                                                iconOnly
                                                leadingIcon={Icon.Condition}
                                                /*
                                                    Because this Button is nested under a link we have to prevent default on the click.

                                                    TODO: We need to improve the DOM structure here because an <a> with <button> nested is not valid
                                                    and can cause accessibility issues. https://linear.app/gitbook-x/issue/RND-1633/toc-structure-issues
                                                */
                                                onClick={(e) => e.preventDefault()}
                                                onPress={onClickPageCondition}
                                                className={clsx(styles.menuItemButton)}
                                            />
                                        ) : null
                                    }
                                    {
                                        // Page commands palette button
                                        !readOnly ? (
                                            <Button
                                                label="More actions"
                                                ref={commandsButtonRef}
                                                kind="blank"
                                                size="small"
                                                leadingIcon={Icon.MoreVertical}
                                                iconOnly
                                                active={paletteHandle.opened}
                                                /*
                                                    Because this Button is nested under a link we have to prevent default on the click.

                                                    TODO: We need to improve the DOM structure here because an <a> with <button> nested is not valid
                                                    and can cause accessibility issues. https://linear.app/gitbook-x/issue/RND-1633/toc-structure-issues
                                                */
                                                onClick={(e) => e.preventDefault()}
                                                onPress={async () => {
                                                    paletteHandle.open(palette());
                                                }}
                                                className={clsx(
                                                    styles.menuItemButton,
                                                    styles.actionsButton,
                                                    {
                                                        [styles.active]:
                                                            interactive.hovered ||
                                                            paletteHandle.opened,
                                                    }
                                                )}
                                            />
                                        ) : null
                                    }
                                </>
                            ) : null
                        }
                    >
                        <span className="hyphens-auto break-words">{page.title}</span>
                        {computedPage ? (
                            <Icon.Zap className="ml-1 size-3 shrink-0 text-muted" />
                        ) : null}
                        {hasSubPages && page.type === hiveCore.RevisionPageType.Document ? (
                            <Button
                                label={`${opened ? 'Collapse' : 'Expand'} sub-pages`}
                                size="small"
                                kind="nested"
                                iconOnly
                                leadingIcon={Icon.Caret}
                                /*
                                    Because this Button is nested under a link we have to prevent default on the click.

                                    TODO: We need to improve the DOM structure here because an <a> with <button> nested is not valid
                                    and can cause accessibility issues. https://linear.app/gitbook-x/issue/RND-1633/toc-structure-issues
                                */
                                onClick={(e) => e.preventDefault()}
                                onPress={() => {
                                    setOpened(!opened);
                                }}
                                className={clsx(
                                    'm-nudge-icon ml-1.5 shrink-0',
                                    styles.menuItemButton,
                                    opened && styles.menuItemExpanded,
                                    (interactive.hovered || paletteHandle.opened) && styles.active
                                )}
                            />
                        ) : null}
                    </ToCMenuItem>
                    {isLast &&
                    !hasChildrenDisplayed &&
                    !readOnly &&
                    (!computedPage || isComputedRoot) ? (
                        <ToCInsertionLine
                            page={dropTargetPage}
                            position="after"
                            visible={interactive.hovered && interactive.hoverZone.bottom}
                            canInsertGroup={canInsertGroup}
                        />
                    ) : null}
                </DOMView>
                {hasChildrenDisplayed ? (
                    <div
                        className={clsx(
                            'relative',
                            page.type === hiveCore.RevisionPageType.Group ? 'ml-0' : 'ml-4'
                        )}
                    >
                        {page.type === hiveCore.RevisionPageType.Group ? null : (
                            <div
                                className={clsx(
                                    'flex',
                                    highlighting ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInBaseSubtreeModifiedInHead &&
                                        styles.tocItemChildrenLineConflicted,
                                    highlighting ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInHeadSubtreeModifiedInBase &&
                                        styles.tocItemChildrenLineConflicted
                                )}
                            />
                        )}
                        <PagesRenderer
                            space={space}
                            pages={subPages}
                            readOnly={readOnly}
                            analyticsContext={analyticsContext}
                            showHiddenPages={showIfHidden}
                            renderPage={(pageProps) => {
                                return (
                                    <PageToCItem
                                        {...props}
                                        {...pageProps}
                                        key={pageProps.key}
                                        isParentHidden={hidden}
                                        flatConflictedSubtree={flatConflictedSubtree}
                                        parent={page}
                                    />
                                );
                            }}
                        />
                    </div>
                ) : null}
            </DropArea>
        </div>
    );
}

function getVisibilityProps(props: {
    visiblityStatus: 'hidden' | 'visible';
    indexStatus: 'no-index' | 'no-robots-index' | null;
}) {
    switch (props.visiblityStatus) {
        case 'hidden': {
            switch (props.indexStatus) {
                case 'no-index':
                    return {
                        icon: Icon.HiddenAndNoIndex,
                        label: 'This page is hidden from navigation and not indexed in the search, Ask AI and by web crawlers (Google, ChatGPT, etc.).',
                    };
                case 'no-robots-index':
                    return {
                        icon: Icon.HiddenAndNoIndex,
                        label: 'This page is hidden from navigation and not indexed by search engines and web-crawlers (Google, ChatGPT, etc.).',
                    };
                case null:
                    return {
                        icon: Icon.EyeOff,
                        label: 'This page is hidden from navigation, but indexed in the search, Ask AI and by web crawlers.',
                    };
                default:
                    assertNever(props.indexStatus);
            }
            break;
        }
        case 'visible':
            switch (props.indexStatus) {
                case 'no-index':
                    return {
                        icon: Icon.NoIndex,
                        label: 'This page it not indexed in the search, Ask AI and by web crawlers (Google, ChatGPT, etc.).',
                    };
                case 'no-robots-index':
                    return {
                        icon: Icon.NoIndex,
                        label: 'This page it not indexed by search engines and web-crawlers (Google, ChatGPT, etc.).',
                    };
                case null:
                    return null;
                default:
                    assertNever(props.indexStatus);
            }
            break;
        default:
            assertNever(props.visiblityStatus);
    }
}

function getTreePageIndexStatus(
    revision: hiveCore.Revision,
    treePage: hiveCore.RevisionTreePage<hiveCore.RevisionPage>
): 'no-index' | 'no-robots-index' | null {
    if (
        hiveCore.isRevisionPageLink(treePage.page) ||
        hiveCore.isRevisionPageComputed(treePage.page) ||
        treePage.diff === hiveCore.PageDiffStatus.Deleted
    ) {
        return null;
    }

    const visibility = hiveCore.getCachedPageVisibilityInRevision(revision, treePage.page.id);

    if (visibility.noIndex.value) {
        return 'no-index';
    }

    if (visibility.noRobotsIndex.value) {
        return 'no-robots-index';
    }

    return null;
}

type PageToCHighlighting =
    | hiveCore.PageDiffStatus
    | hiveCore.RevisionConflictState
    | 'conflict-resolved'
    | 'deleted-in-conflicted-subtree'
    | 'modified-inside'
    | null;

/**
 * Compute the diff or conflict highlighting for a page.
 * Taking children into consideration when the page is closed.
 */
function getHighlightingForPage(
    revision: hiveCore.Revision,
    treePage: hiveCore.RevisionTreePage,
    flatConflictedSubtree: hiveCore.RevisionPage[]
): PageToCHighlighting {
    const pageInConflictedSubtree = flatConflictedSubtree.find(
        (page) => page.id === treePage.page.id
    );

    if (
        revision.conflicts &&
        (treePage.page.id in revision.conflicts.pages || pageInConflictedSubtree)
    ) {
        const conflict = revision.conflicts.pages[treePage.page.id];
        if (conflict === undefined) {
            return null;
        }

        if (conflict === false) {
            return 'conflict-resolved';
        }

        if (conflict !== hiveCore.RevisionConflictState.DocumentConflict) {
            return conflict;
        }

        if (pageInConflictedSubtree) {
            return 'deleted-in-conflicted-subtree';
        }

        return conflict;
    }

    if (treePage.diff) {
        return treePage.diff ?? null;
    }

    // When page is closed, we show it as modified if one of its children has diff.
    if (treePage.page.type !== hiveCore.RevisionPageType.Group) {
        const descendants = treePage.pages
            .map((page) => getHighlightingForPage(revision, page, flatConflictedSubtree))
            .filter(filterOutNullable);

        if (descendants.includes(hiveCore.RevisionConflictState.DocumentConflict)) {
            return hiveCore.RevisionConflictState.DocumentConflict;
        }
        if (descendants.includes('conflict-resolved')) {
            return null;
        }

        if (descendants.length > 0) {
            return 'modified-inside';
        }
    }

    return null;
}

const ConflictMergeBadgeIcon = () => <Badge kind="merge" icon={Icon.Conflict} size="small" />;
const TrashDangerBadgeIcon = () => <Badge kind="danger" icon={Icon.Trashcan} size="small" />;
const ConflictWarningBadgeIcon = () => <Badge kind="warning" icon={Icon.Conflict} size="small" />;
const CheckSuccessBadgeIcon = () => <Badge kind="success" icon={Icon.Check} size="small" />;

/**
 * Get the icon for the highlighting computed in highlighting.
 */
function getIconForHighlighting(highlighting: PageToCHighlighting): IconDefinition | undefined {
    switch (highlighting) {
        case hiveCore.PageDiffStatus.Added:
            return Icon.DiffAdded;
        case hiveCore.PageDiffStatus.Modified:
            return Icon.DiffModified;
        case 'modified-inside':
            return Icon.DiffModifiedInside;
        case hiveCore.PageDiffStatus.Deleted:
            return Icon.DiffRemoved;
        case hiveCore.RevisionConflictState.DocumentConflict:
            return ConflictMergeBadgeIcon;
        case hiveCore.RevisionConflictState.DeletedInBase:
        case hiveCore.RevisionConflictState.DeletedInHead:
            return TrashDangerBadgeIcon;
        case hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead:
        case hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase:
            return ConflictWarningBadgeIcon;
        case 'conflict-resolved':
            return CheckSuccessBadgeIcon;
        case 'deleted-in-conflicted-subtree':
        case null:
            return undefined;
        default:
            assertNever(highlighting, `Unknown highlighting: ${highlighting}`);
    }
}

function getClassNameForHighlighting(highlighting: PageToCHighlighting): string | undefined {
    const baseClass = 'size-5 opacity-50';
    switch (highlighting) {
        case hiveCore.PageDiffStatus.Added:
        case hiveCore.PageDiffStatus.Modified:
        case hiveCore.PageDiffStatus.Deleted:
            return clsx(baseClass, DIFF_TEXT_COLORS[highlighting]);
        case 'modified-inside':
            return clsx(baseClass, DIFF_TEXT_COLORS.modified);
        default:
            return undefined;
    }
}

/**
 * Get text for the changes' tooltip.
 */
function getChangesTooltipText(highlighting: PageToCHighlighting): string | undefined {
    switch (highlighting) {
        case hiveCore.PageDiffStatus.Added:
            return 'This page has been added in this change request';
        case hiveCore.PageDiffStatus.Modified:
            return 'This page has been modified in this change request';
        case hiveCore.PageDiffStatus.Deleted:
            return 'This page has been deleted in this change request';
    }
}
